import React, { useEffect, useState } from "react";import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Download, ShieldCheck, Send, Link as LinkIcon } from "lucide-react";
import { DoxMascot } from "@/components/Logo";
import api, { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * /verify/:recordId — the public "Dox completed this." page.
 *
 * Outcome-first. Not a dashboard. Not navigation. Not a features tour.
 * Just proof: what Dox did, on what date, and a bank-quality integrity check.
 * No login required.
 */
export default function Verify() {
  const { recordId } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/verify/${recordId}`)
      .then((r) => { if (live) setRecord(r.data); })
      .catch((e) => {
        if (!live) return;
        if (e?.response?.status === 404) setError("This receipt could not be found.");
        else setError("This receipt could not be verified right now.");
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [recordId]);

  const pdfUrl = `${API}/verify/${recordId}/pdf`;

  return (
    <div className="min-h-screen bg-[#050812] text-white">
      {/* Ambient depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-30 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(30,120,255,0.35), transparent 60%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -bottom-56 -left-40 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, rgba(90,168,255,0.35), transparent 60%)" }}
      />

      <div className="relative max-w-3xl mx-auto px-6 py-20 sm:py-28">
        {loading && <LoadingState />}
        {!loading && error && <ErrorState message={error} />}
        {!loading && record && <ProofScreen record={record} pdfUrl={pdfUrl} />}
      </div>

      <footer className="relative pt-16 pb-10 text-center text-[11px] text-white/35 tracking-[0.22em] uppercase">
        Verified by EZDoxAI
      </footer>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center pt-20 text-center">
      <DoxMascot size={110} emotion="thinking" />
      <div className="mt-10 text-white/50 text-sm tracking-wide">Confirming…</div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="flex flex-col items-center pt-20 text-center">
      <DoxMascot size={110} emotion="idle" />
      <h1 className="mt-10 font-display font-black text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.05] max-w-lg">
        {message}
      </h1>
      <p className="mt-6 text-white/55 text-base max-w-md">
        Double-check the link, or ask the person who shared it to send it again.
      </p>
    </div>
  );
}

function ProofScreen({ record, pdfUrl }) {
  const isPreview = record.mode === "preview";
  const verified = !!record.verified;
  const c = record.case || {};
  const issued = formatDate(record.issued_at);

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.33, 1, 0.68, 1] }}
    >
      {/* Hero */}
      <div className="flex flex-col items-center text-center">
        <VerifiedSeal verified={verified} isPreview={isPreview} />
        <h1
          data-testid="verify-title"
          className="mt-10 font-display font-black text-white text-5xl sm:text-6xl lg:text-7xl tracking-[-0.025em] leading-[1.02]"
        >
          Dox completed this.
        </h1>
        <p className="mt-8 text-white/70 text-lg sm:text-xl leading-relaxed max-w-xl">
          {"A "}
          <span className="text-white font-medium">{c.classification || "renewal"}</span>
          {c.jurisdiction ? <> in <span className="text-white font-medium">{c.jurisdiction}</span></> : null}
          {c.authority ? <> handled with <span className="text-white font-medium">{c.authority}</span></> : null}
          {issued ? <> on <span className="text-white font-medium">{issued}</span>.</> : "."}
        </p>

        {isPreview && (
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-4 py-2 text-amber-300 text-xs tracking-wide">
            This was a Dox Preview — no payment, no filing.
          </div>
        )}

        <a
          data-testid="verify-download-pdf"
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-14 inline-flex items-center gap-2 rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-semibold text-base px-9 py-4 min-h-[52px] shadow-[0_10px_40px_rgba(30,120,255,0.35)] transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          <Download size={16} strokeWidth={2.5} /> Save the receipt
        </a>
        <ShareRow recordId={record.record_id} />
      </div>

      {/* What Dox did — serene timeline */}
      <div className="mt-24">
        <div className="text-[11px] uppercase tracking-[0.28em] text-white/40 font-bold text-center">
          What Dox did
        </div>
        <div className="mt-10 space-y-8 max-w-2xl mx-auto">
          {(record.trace || []).map((s, i) => (
            <motion.div
              key={s.step || i}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.05, duration: 0.4 }}
              className="flex items-start gap-5"
              data-testid={`verify-step-${s.code}`}
            >
              <span className="mt-1 h-7 w-7 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center shrink-0">
                <Check size={13} strokeWidth={3} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-display font-black text-white text-lg leading-tight">{s.headline || s.label}</div>
                {s.detail && (
                  <div className="mt-1 text-white/55 text-sm leading-relaxed">{s.detail}</div>
                )}
                <div className="mt-1 text-white/30 text-[11px] tracking-wider">{formatDate(s.ts, true)}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Integrity — quiet reassurance */}
      <div className="mt-24 rounded-2xl border border-white/10 bg-white/[0.03] px-6 sm:px-8 py-6 sm:py-7">
        <div className="flex items-start gap-4">
          <span className={`mt-0.5 h-9 w-9 rounded-full flex items-center justify-center ${verified ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
            <ShieldCheck size={17} strokeWidth={2.5} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-white text-base">
              {verified ? "Verified authentic." : "Integrity mismatch."}
            </div>
            <div className="mt-1 text-white/55 text-sm leading-relaxed">
              {verified
                ? "Every step is hash-signed and independently verifiable."
                : "The stored integrity root does not match the recomputed chain. Contact Dox."}
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-[12px]">
              <MetaRow label="Record" value={record.record_id} mono />
              <MetaRow label="Integrity root" value={truncateMid(record.integrity_root, 22)} mono />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function VerifiedSeal({ verified, isPreview }) {
  const tone = isPreview ? "amber" : verified ? "emerald" : "rose";
  const label = isPreview ? "Preview" : verified ? "Verified" : "Unverified";
  const styles = {
    emerald: "text-emerald-300 border-emerald-400/30 bg-emerald-500/[0.08]",
    amber: "text-amber-300 border-amber-400/30 bg-amber-500/[0.08]",
    rose: "text-rose-300 border-rose-400/30 bg-rose-500/[0.08]",
  }[tone];
  return (
    <div className="flex flex-col items-center gap-6">
      <DoxMascot size={112} emotion="done" />
      <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[11px] uppercase tracking-[0.28em] font-bold ${styles}`}>
        <span className="h-1.5 w-1.5 rounded-full bg-current" /> {label}
      </div>
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <div>
      <div className="text-white/35 uppercase tracking-[0.2em] text-[10px]">{label}</div>
      <div className={`text-white/90 mt-0.5 ${mono ? "font-mono text-[11px] break-all" : "text-sm"}`}>{value || "—"}</div>
    </div>
  );
}

function formatDate(iso, withTime = false) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const opts = { year: "numeric", month: "long", day: "numeric" };
    if (withTime) { opts.hour = "numeric"; opts.minute = "2-digit"; }
    return d.toLocaleString(undefined, opts);
  } catch {
    return "";
  }
}

function truncateMid(str, keep = 16) {
  if (!str || str.length <= keep * 2) return str || "";
  return `${str.slice(0, keep)}…${str.slice(-6)}`;
}

/**
 * Dox Sent Proof — one-tap share. Two ways: send by email, or copy the
 * public link. Kept quiet and low-chrome so the proof stays the star.
 */
function ShareRow({ recordId }) {
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const publicUrl = typeof window !== "undefined" ? `${window.location.origin}/verify/${recordId}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  const send = async () => {
    if (!email) return;
    setSending(true);
    try {
      await api.post(`/verify/${recordId}/share`, { to: email });
      toast.success("Sent.");
      setShowEmail(false);
      setEmail("");
    } catch {
      toast.error("Sign in to send by email — or copy the link instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
      <button
        data-testid="verify-share-email"
        onClick={() => setShowEmail((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 px-4 py-2 text-sm text-white/85 transition"
      >
        <Send size={13} /> Send by email
      </button>
      <button
        data-testid="verify-share-copy"
        onClick={copy}
        className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 px-4 py-2 text-sm text-white/85 transition"
      >
        <LinkIcon size={13} /> Copy link
      </button>
      {showEmail && (
        <div className="w-full flex justify-center mt-2">
          <div className="flex items-center gap-2 max-w-md w-full">
            <input
              data-testid="verify-share-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="client@company.com"
              className="flex-1 rounded-full bg-white/[0.04] border border-white/10 px-4 py-2 text-sm text-white/90 placeholder:text-white/30 focus:outline-none focus:border-dox-orange/50"
            />
            <button
              data-testid="verify-share-send"
              onClick={send}
              disabled={sending || !email}
              className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-semibold text-sm px-5 py-2 disabled:opacity-50 transition"
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
